// imports
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMMAHandlers } from '@/ui/advancedAssistants/MMA/useMMAHandlers'
import { useMMAState } from '@/ui/advancedAssistants/MMA/useMMAState'
import { useSpace } from '@/hooks/useSpace'
import type { InternalMessage } from '@/utils/ai-adapters/xdc/types'
import type { AnswerAreaProps, UserTextMessage } from './types/AnswerAreaTypes'

// components
import AssistantMessage from '@/ui/components/ChatMessageTypes/Assistant/AssistantMessage'
import UserMessage from '@/ui/components/ChatMessageTypes/User/UserMessage'
import { DisplayMode } from '@/ui/types/DisplayMode'
import { Button } from '@bosch/react-frok/lib/esm/atoms/button/button'
import QuickLineSelection from '@/ui/components/QuickLineSelection/QuickLineSelection'

// utils
import utils from '@/utils/utils'
import Badge from '@/ui/components/Badge/Badge'
import OverlayScrollbar from './OverlayScrollbar'
import useWindowSize from '@/hooks/useWindowSize'

// styles
import './AnswerArea.scss'

const SCROLL_BOTTOM_THRESHOLD = 100

const isUserMessage = (message: InternalMessage): message is UserTextMessage => message.role === 'user'

const AnswerArea = ({ chatId, messageList, features, waitingForResponse, isDarkTheme, activeAssistantConfig, userImage, currentFilters, availableLines, onToggleLine }: AnswerAreaProps) => {
    const { t } = useTranslation()
    const [isAtBottom, setIsAtBottom] = useState(true)

    const containerRef = useRef<HTMLDivElement>(null)
    const prevUserCountRef = useRef(0)
    // a message was sent and its answer has not appeared yet
    const awaitsAnswerRef = useRef(false)
    const hasScrolledInitiallyRef = useRef(false)

    const { getActiveAssistant } = useSpace()
    const assistant = getActiveAssistant()
    const mmaState = useMMAState()
    const { handlers } = useMMAHandlers(mmaState)
    const { activeMessageList } = mmaState
    const { isMobile } = useWindowSize()
    const [isGlobalSearchClicked, setIsGlobalSearchClicked] = useState<boolean>(false)

    useEffect(() => {
        const container = containerRef.current
        const inputArea = container?.closest('.chat-root')?.querySelector('.input-area') as HTMLElement | null
        if (!container || !inputArea) return

        const updateOverlap = () => requestAnimationFrame(() => {
            const overlap = Math.max(0, container.getBoundingClientRect().bottom - inputArea.getBoundingClientRect().top)
            container.style.setProperty('--input-area-overlap', `${overlap}px`)
        })

        const observer = new ResizeObserver(updateOverlap)
        observer.observe(inputArea)
        observer.observe(container)
        return () => observer.disconnect()
    }, [])

    const handleContainerScroll = useCallback(() => {
        const el = containerRef.current
        if (!el) return
        setIsAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD)
    }, [])

    useEffect(() => {
        hasScrolledInitiallyRef.current = false
        prevUserCountRef.current = 0
        awaitsAnswerRef.current = false
    }, [chatId])

    useEffect(() => {
        const userCount = messageList.filter(m => m.role === 'user').length
        // Opening a chat jumps to the bottom; after a send it scrolls there smoothly (unless the user prefers reduced motion)
        const scrollToEnd = (smooth: boolean) => requestAnimationFrame(() => {
            const container = containerRef.current
            if (!container) return
            const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
            container.scrollTo({ top: container.scrollHeight, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' })
        })

        if (!hasScrolledInitiallyRef.current) {
            if (messageList.length > 0) {
                hasScrolledInitiallyRef.current = true
                scrollToEnd(false)
            }
        } else if (userCount > prevUserCountRef.current) {
            awaitsAnswerRef.current = true
            scrollToEnd(true)
        }

        // The answer to a sent message appears: scroll to it once. The streaming after it does not scroll, so the
        // user can read and scroll at their own pace.
        if (awaitsAnswerRef.current && messageList[messageList.length - 1]?.role === 'assistant') {
            awaitsAnswerRef.current = false
            scrollToEnd(true)
        }

        prevUserCountRef.current = userCount
        requestAnimationFrame(() => handleContainerScroll())
    }, [messageList, handleContainerScroll])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return
        container.addEventListener('scroll', handleContainerScroll, { passive: true })
        return () => container.removeEventListener('scroll', handleContainerScroll)
    }, [handleContainerScroll])

    const scrollToBottom = useCallback(() => {
        containerRef.current?.scrollTo({ top: containerRef.current.scrollHeight, behavior: 'smooth' })
    }, [])

    useEffect(() => {
        setIsGlobalSearchClicked(false)
    }, [assistant?.activeChat?.chatId])

    const isGlobalRequestRef = useRef(false)
    const isGlobalInProgressRef = useRef(false)
    const prevWaitingRef = useRef(false)

    // Reset as soon as global search stream finishes (finishedMessage on last msg).
    // isGlobalInProgressRef guards against the previous local-search's finishedMessage=true
    // triggering a reset at the moment of the click (before streaming begins).
    useEffect(() => {
        if (!isGlobalSearchClicked || !isGlobalInProgressRef.current) return
        const lastMsg = messageList[messageList.length - 1]
        if (lastMsg?.role === 'assistant' && lastMsg?.finishedMessage === true) {
            isGlobalInProgressRef.current = false
            setIsGlobalSearchClicked(false)
        }
    }, [messageList, isGlobalSearchClicked])

    // false --> true transition only: global search --> mark in-progress; regular query --> clear flag
    useEffect(() => {
        const justStarted = !!waitingForResponse && !prevWaitingRef.current
        prevWaitingRef.current = !!waitingForResponse
        if (!justStarted) return
        if (isGlobalRequestRef.current) {
            isGlobalRequestRef.current = false
            isGlobalInProgressRef.current = true
        } else {
            setIsGlobalSearchClicked(false)
        }
    }, [waitingForResponse])

    // Read through a ref: the rendered messages are memoized, so a plain closure would keep the lines of the last render
    const globalSearchRef = useRef<() => void>(() => {})
    globalSearchRef.current = (): void => {
        isGlobalRequestRef.current = true
        setIsGlobalSearchClicked(true)

        const lastUserMessage = activeMessageList
            ?.filter(isUserMessage)
            ?.pop()

        const lineParam = currentFilters?.line?.map((line: { value: string }) => line.value).join(',') ?? ''

        handlers.advancedSendRequest({
            message: lastUserMessage?.content || lastUserMessage?.message || '',
            isGlobalSearch: true,
            line: lineParam,
        })
    }

    // Always keep the ref current so the stable callback below reads the latest values.
    const retryRef = useRef<() => void>(() => {})
    retryRef.current = () => {
        const list = activeMessageList || []
        const lastUserMessage = [...list].reverse().find(isUserMessage)
        if (lastUserMessage?.content || lastUserMessage?.message) {
            handlers.advancedSendRequest({
                message: lastUserMessage?.content || lastUserMessage?.message || '',
                isRetry: true,
            })
        }
    }
    // Stable reference — never changes, so it is safe to include in useMemo deps
    // without causing unnecessary re-renders.
    const stableRetry = useCallback(() => retryRef.current(), [])
    const handleGlobalSearchClick = useCallback(() => globalSearchRef.current(), [])

    const renderedMessages = useMemo(() => {
        let lastAssistantMessageIndex = [...messageList]
            .reverse()
            .findIndex((message) => message.role === 'assistant')

        if (lastAssistantMessageIndex !== -1)
            lastAssistantMessageIndex = messageList.length - 1 - lastAssistantMessageIndex

        return messageList.map((message, index) => {
            switch (message.role) {
                case 'assistant':
                    return (
                        <AssistantMessage
                            key={index}
                            activeAssistantConfig={activeAssistantConfig}
                            message={message}
                            messageList={messageList}
                            messageIndex={index}
                            lastAssistantMessageIndex={lastAssistantMessageIndex}
                            features={index !== 0 ? features : undefined}
                            isDarkTheme={isDarkTheme}
                            onRetry={stableRetry}
                            isGlobalSearchClicked={isGlobalSearchClicked}
                            onGlobalSearchClick={handleGlobalSearchClick}
                            isWelcomeMessage={index === 0}
                            waitingForResponse={waitingForResponse}
                        />
                    )
                case 'user':
                    return (
                        <div key={`${message.messageId}-${index}`}>
                            <UserMessage
                                message={message}
                                userImage={userImage}
                                activeAssistantConfig={activeAssistantConfig}
                            />
                        </div>
                    )
                default:
                    return null
            }
        })
    }, [messageList, isGlobalSearchClicked, stableRetry, handleGlobalSearchClick, isDarkTheme, activeAssistantConfig, features, userImage, waitingForResponse])

    return (
        <div className="answer-area-root" ref={containerRef}>
            <OverlayScrollbar containerRef={containerRef} />
            {isMobile && <Badge badges={utils.getAssistantBadges(assistant)} mode={DisplayMode.MOBILE} position="top-right" />}
            <div className="answer-area-wrapper">
                {renderedMessages}
                {availableLines && onToggleLine && (
                    <QuickLineSelection
                        currentFilters={currentFilters}
                        availableLines={availableLines}
                        onToggleLine={onToggleLine}
                        isDisabled={waitingForResponse}
                        isHidden={waitingForResponse}
                    />
                )}
            </div>
            <Button
                icon={{ iconName: 'down' }}
                className={`scroll-to-bottom-btn${isAtBottom ? ' scroll-to-bottom-btn--hidden' : ''}`}
                onClick={scrollToBottom}
                aria-label={t('chat:scrollToBottom')}
            />
        </div>
    )
}

export default AnswerArea
